import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader, Plus, X } from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useVerticalLabel } from '../../hooks/useVerticalCatalog';
import { unwrapArray } from '../../lib/api-envelope';
import { LEAD_SOURCE_LABELS, isLeadSource } from '../../lib/opportunity';
import { CustomerTypeahead, type PickedCustomer } from './CustomerTypeahead';
import {
  LEAD_SOURCES,
  type LeadSource,
  type Opportunity,
  type OpportunityWriteBody,
  type OpportunityWriteResult,
  type Pipeline,
} from '../../types/opportunity';

/**
 * New / edit an opportunity. Body is snake_case (POST/PATCH /opportunities).
 * An empty value field sends `estimated_value: null` — NOT ESTIMATED, never 0.
 * The stage is not edited here: stage moves go through the detail page's
 * stage control, which owns the lost-reason and won-promotion rules.
 *
 * ORDER (pipeline upgrade brief): customer → contact → title → service line →
 * value → probability → expected close + start → next step + due → owner →
 * lead source → "More details" (well, pad/LSD, rig, area, competitor, notes).
 *
 * PROBABILITY. The API's `probability` is EFFECTIVE (override, else the stage
 * default) and `probabilityIsDefault` says which. The field is prefilled with
 * that figure — for a new deal, the lead stage's `defaultProbability` from
 * /opportunities/pipeline, so the default is the API's, not a copy here. The
 * key is SENT ONLY WHEN THE PERSON CHANGED IT: an untouched field leaves an
 * existing override alone and leaves a default a default (so a later stage
 * move still re-defaults it). Cleared after a change sends `null` — back to
 * the stage default.
 *
 * Service line is `vertical`, one of the tenant's ENABLED verticals — the API
 * refuses any other. Rig is `rig_name`.
 */
const inputClass =
  'w-full rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none';
const labelClass = 'mb-1 block text-xs font-semibold text-fieldiq-text';

/** Roles that can own a deal in practice — the API's CRM_ROLES (crm-roles.ts). The API accepts any tenant user. */
const OWNER_ROLES = new Set(['admin', 'office_staff', 'supervisor', 'dispatcher', 'sales']);

interface ContactRow { id: string; name: string | null; phone?: string | null; email?: string | null; role?: string | null }
interface UserRow { id: string; fullName?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null; role?: string | null }
const userName = (u: UserRow) => u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '—';

const blank = (v: string) => (v.trim() === '' ? null : v.trim());

export function OpportunityFormDialog({
  open,
  onClose,
  existing,
  initialCustomer,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  existing?: Opportunity | null;
  initialCustomer?: PickedCustomer | null;
  onSaved?: (row: OpportunityWriteResult) => void;
}) {
  const queryClient = useQueryClient();
  const { user, company } = useAuth();
  const verticalLabel = useVerticalLabel();
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [contactId, setContactId] = useState('');
  const [title, setTitle] = useState('');
  const [vertical, setVertical] = useState('');
  const [value, setValue] = useState('');
  const [probability, setProbability] = useState('');
  const [probabilityTouched, setProbabilityTouched] = useState(false);
  const [expectedClose, setExpectedClose] = useState('');
  const [expectedStart, setExpectedStart] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [nextStepDue, setNextStepDue] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [leadSource, setLeadSource] = useState<LeadSource | ''>('');
  const [wellName, setWellName] = useState('');
  const [padOrLsd, setPadOrLsd] = useState('');
  const [rigName, setRigName] = useState('');
  const [area, setArea] = useState('');
  const [competitor, setCompetitor] = useState('');
  const [notes, setNotes] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inline "add contact".
  const [addingContact, setAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '' });
  const [contactError, setContactError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAddingContact(false);
    setContactError(null);
    setNewContact({ name: '', phone: '', email: '' });
    setProbabilityTouched(false);
    const e = existing;
    setCustomer(e ? { id: e.customerId, name: e.customerName ?? '—', status: e.customerStatus } : initialCustomer ?? null);
    setContactId(e?.contactId ?? '');
    setTitle(e?.title ?? '');
    setVertical(e?.vertical ?? '');
    setValue(e && e.estimatedValue !== null ? String(e.estimatedValue) : '');
    setProbability(e && Number.isFinite(e.probability) ? String(e.probability) : '');
    setExpectedClose(e?.expectedCloseDate ?? '');
    setExpectedStart(e?.expectedStart ?? '');
    setNextStep(e?.nextStep ?? '');
    setNextStepDue(e?.nextStepDue ?? '');
    setOwnerId(e?.ownerUserId ?? user?.id ?? '');
    setLeadSource(e && isLeadSource(e.leadSource) ? e.leadSource : '');
    setWellName(e?.wellName ?? '');
    setPadOrLsd(e?.padOrLsd ?? '');
    setRigName(e?.rigName ?? '');
    setArea(e?.area ?? '');
    setCompetitor(e?.competitor ?? '');
    setNotes(e?.notes ?? '');
    // Open "More details" when editing a deal that already has any of them.
    setMoreOpen(!!e && !!(e.wellName || e.padOrLsd || e.rigName || e.area || e.competitor || e.notes));
  }, [open, existing, initialCustomer, user?.id]);

  // A new deal starts on `lead`; its default probability is the API's.
  const pipelineQuery = useQuery({
    queryKey: ['opportunities', 'pipeline'],
    queryFn: () => api.opportunities.pipeline({ include_closed: 1 }).then((r) => r.data.data as Pipeline),
    enabled: open && !existing,
  });
  const leadDefault = pipelineQuery.data?.stages.find((s) => s.stage === 'lead')?.defaultProbability;
  useEffect(() => {
    if (open && !existing && !probabilityTouched && typeof leadDefault === 'number') setProbability(String(leadDefault));
  }, [open, existing, probabilityTouched, leadDefault]);

  const contactsQuery = useQuery({
    queryKey: ['customer-contacts', customer?.id],
    queryFn: () => api.customers.listContacts(customer!.id).then((r) => unwrapArray<ContactRow>(r.data)),
    enabled: open && !!customer?.id,
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'list', 'opportunity-owner'],
    queryFn: () => api.users.list().then((r) => unwrapArray<UserRow>(r.data)),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const owners = useMemo(() => {
    const rows = (usersQuery.data ?? []).filter((u) => OWNER_ROLES.has(String(u.role)) || u.id === ownerId);
    return [...rows].sort((a, b) => userName(a).localeCompare(userName(b)));
  }, [usersQuery.data, ownerId]);

  const enabledVerticals: string[] = company?.enabledVerticals ?? company?.enabled_verticals ?? [];
  // An existing deal's vertical stays selectable even if it has since been switched off.
  const verticalOptions = vertical && !enabledVerticals.includes(vertical) ? [...enabledVerticals, vertical] : enabledVerticals;

  const valueTrim = value.trim();
  const valueNum = valueTrim === '' ? null : Number(valueTrim);
  const valueInvalid = valueNum !== null && (!Number.isFinite(valueNum) || valueNum < 0);
  const probTrim = probability.trim();
  const probNum = probTrim === '' ? null : Number(probTrim);
  const probInvalid = probNum !== null && (!Number.isInteger(probNum) || probNum < 0 || probNum > 100);

  const addContact = useMutation({
    mutationFn: () =>
      api.customers
        .createContact(customer!.id, { name: newContact.name.trim(), phone: blank(newContact.phone), email: blank(newContact.email) })
        .then((r) => (r.data?.data ?? r.data) as ContactRow),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['customer-contacts', customer?.id] });
      if (row?.id) setContactId(row.id);
      setAddingContact(false);
      setNewContact({ name: '', phone: '', email: '' });
    },
    onError: (err) => setContactError(getApiErrorMessage(err, 'Could not add the contact.')),
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const body: OpportunityWriteBody = {
        title: title.trim(),
        contact_id: contactId || null,
        vertical: vertical || null,
        estimated_value: valueNum,
        expected_close_date: expectedClose || null,
        expected_start: expectedStart || null,
        next_step: blank(nextStep),
        next_step_due: nextStepDue || null,
        lead_source: leadSource || null,
        well_name: blank(wellName),
        pad_or_lsd: blank(padOrLsd),
        rig_name: blank(rigName),
        area: blank(area),
        competitor: blank(competitor),
        notes: blank(notes),
      };
      if (ownerId) body.owner_user_id = ownerId;
      if (probabilityTouched) body.probability = probNum;
      if (existing) return api.opportunities.update(existing.id, body).then((r) => r.data.data as OpportunityWriteResult);
      return api.opportunities.create({ ...body, customer_id: customer!.id }).then((r) => r.data.data as OpportunityWriteResult);
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunity', row?.id] });
      onSaved?.(row);
      onClose();
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Could not save the opportunity.')),
  });

  if (!open) return null;
  const canSave = !!customer && !!title.trim() && !valueInvalid && !probInvalid && !mutation.isPending;
  const contacts = contactsQuery.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center" onClick={(e) => { if (e.target === e.currentTarget && !mutation.isPending) onClose(); }}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="opp-form-title"
        onSubmit={(e) => { e.preventDefault(); if (canSave) mutation.mutate(); }}
        className="flex max-h-[92vh] w-full max-w-xl flex-col rounded-t-2xl bg-fieldiq-card shadow-2xl sm:mx-4 sm:rounded-2xl"
      >
        <div className="flex items-center justify-between border-b border-fieldiq-border px-5 py-4">
          <h3 id="opp-form-title" className="text-lg font-bold text-white">{existing ? 'Edit opportunity' : 'New opportunity'}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-fieldiq-muted hover:text-white"><X size={20} /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-auto px-5 py-4">
          <div>
            <label htmlFor="meeting-customer" className={labelClass}>Customer *</label>
            <CustomerTypeahead value={customer} onChange={(c) => { setCustomer(c); setContactId(''); setAddingContact(false); }} disabled={!!existing} />
          </div>

          <div>
            <label htmlFor="opp-contact" className={labelClass}>Contact</label>
            <div className="flex gap-2">
              <select
                id="opp-contact"
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                disabled={!customer}
                className={inputClass}
              >
                <option value="">
                  {!customer ? 'Pick a customer first' : contactsQuery.isLoading ? 'Loading contacts…' : contactsQuery.isError ? 'Could not load contacts' : contacts.length ? 'No contact' : 'No contacts on file'}
                </option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{[c.name ?? '—', c.role].filter(Boolean).join(' · ')}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={!customer}
                onClick={() => { setAddingContact((a) => !a); setContactError(null); }}
                aria-expanded={addingContact}
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                <Plus size={14} aria-hidden="true" /> Add contact
              </button>
            </div>
            {addingContact && customer && (
              <div className="mt-2 space-y-2 rounded-lg border border-fieldiq-border bg-fieldiq-dark/50 p-3" role="group" aria-label="New contact">
                <input aria-label="Contact name" placeholder="Name *" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} className={inputClass} />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input aria-label="Contact phone" placeholder="Phone" inputMode="tel" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className={inputClass} />
                  <input aria-label="Contact email" placeholder="Email" inputMode="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className={inputClass} />
                </div>
                {contactError && <p role="alert" className="text-xs text-fieldiq-text">{contactError}</p>}
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setAddingContact(false)} className="px-3 py-1.5 text-sm text-fieldiq-muted">Cancel</button>
                  <button
                    type="button"
                    disabled={!newContact.name.trim() || addContact.isPending}
                    onClick={() => addContact.mutate()}
                    className="rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {addContact.isPending ? 'Saving…' : 'Save contact'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="opp-title" className={labelClass}>Title *</label>
            <input id="opp-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} placeholder="Ensign 12 — 7in casing program" />
          </div>

          <div>
            <label htmlFor="opp-vertical" className={labelClass}>Service line</label>
            <select id="opp-vertical" value={vertical} onChange={(e) => setVertical(e.target.value)} className={inputClass}>
              <option value="">Not set</option>
              {verticalOptions.map((v) => <option key={v} value={v}>{verticalLabel(v)}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="opp-value" className={labelClass}>Estimated value</label>
              <input id="opp-value" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} className={inputClass} placeholder="Not estimated" />
              {valueInvalid && <p className="mt-1 text-xs text-fieldiq-text">Enter a number of 0 or more, or leave it blank.</p>}
            </div>
            <div>
              <label htmlFor="opp-probability" className={labelClass}>Probability %</label>
              <input
                id="opp-probability"
                inputMode="numeric"
                value={probability}
                onChange={(e) => { setProbability(e.target.value); setProbabilityTouched(true); }}
                className={inputClass}
                placeholder="Stage default"
              />
              <p className="mt-1 text-xs text-fieldiq-muted">
                {probInvalid
                  ? 'A whole number from 0 to 100.'
                  : probabilityTouched
                    ? probTrim === '' ? 'Blank goes back to the stage default.' : 'Your figure; a stage move resets it.'
                    : existing && !existing.probabilityIsDefault ? 'Set by hand.' : 'Stage default — change it if you know better.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="opp-close" className={labelClass}>Expected close</label>
              <input id="opp-close" type="date" value={expectedClose} onChange={(e) => setExpectedClose(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label htmlFor="opp-start" className={labelClass}>Expected start</label>
              <input id="opp-start" type="date" value={expectedStart} onChange={(e) => setExpectedStart(e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_11rem]">
            <div>
              <label htmlFor="opp-next-step" className={labelClass}>Next step</label>
              <input id="opp-next-step" value={nextStep} onChange={(e) => setNextStep(e.target.value)} className={inputClass} placeholder="Send revised rates to the drilling super" />
            </div>
            <div>
              <label htmlFor="opp-next-due" className={labelClass}>Due</label>
              <input id="opp-next-due" type="date" value={nextStepDue} onChange={(e) => setNextStepDue(e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="opp-owner" className={labelClass}>Owner</label>
              <select id="opp-owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className={inputClass}>
                {/* Until the list arrives, "me" is the only honest option. */}
                {!owners.some((u) => u.id === ownerId) && ownerId && (
                  <option value={ownerId}>{ownerId === user?.id ? 'Me' : existing?.ownerName ?? '—'}</option>
                )}
                {owners.map((u) => <option key={u.id} value={u.id}>{u.id === user?.id ? `${userName(u)} (me)` : userName(u)}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="opp-lead-source" className={labelClass}>Lead source</label>
              <select id="opp-lead-source" value={leadSource} onChange={(e) => setLeadSource(e.target.value as LeadSource | '')} className={inputClass}>
                <option value="">Not set</option>
                {LEAD_SOURCES.map((s) => <option key={s} value={s}>{LEAD_SOURCE_LABELS[s]}</option>)}
              </select>
            </div>
          </div>

          <div className="rounded-lg border border-fieldiq-border">
            <button
              type="button"
              onClick={() => setMoreOpen((m) => !m)}
              aria-expanded={moreOpen}
              aria-controls="opp-more-details"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-white"
            >
              {moreOpen ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
              More details
              <span className="text-xs font-normal text-fieldiq-muted">Well, pad/LSD, rig, area, competitor, notes</span>
            </button>
            {moreOpen && (
              <div id="opp-more-details" className="space-y-3 border-t border-fieldiq-border px-3 py-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="opp-well" className={labelClass}>Well</label>
                    <input id="opp-well" value={wellName} onChange={(e) => setWellName(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="opp-pad" className={labelClass}>Pad / LSD</label>
                    <input id="opp-pad" value={padOrLsd} onChange={(e) => setPadOrLsd(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="opp-rig" className={labelClass}>Rig</label>
                    <input id="opp-rig" value={rigName} onChange={(e) => setRigName(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="opp-area" className={labelClass}>Area</label>
                    <input id="opp-area" value={area} onChange={(e) => setArea(e.target.value)} className={inputClass} />
                  </div>
                </div>
                <div>
                  <label htmlFor="opp-competitor" className={labelClass}>Competitor</label>
                  <input id="opp-competitor" value={competitor} onChange={(e) => setCompetitor(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="opp-notes" className={labelClass}>Notes</label>
                  <textarea id="opp-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />
                </div>
              </div>
            )}
          </div>

          {error && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-fieldiq-text">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 border-t border-fieldiq-border px-5 py-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-white">Cancel</button>
          <button type="submit" disabled={!canSave} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
            {mutation.isPending && <Loader size={14} className="animate-spin" aria-hidden="true" />}
            {existing ? 'Save' : 'Create opportunity'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default OpportunityFormDialog;
